import React from 'react'
import PropTypes from 'prop-types'
import {AspectRatio, Box, Button, Flex, Img, List, ListItem, Text} from '@chakra-ui/react'
import {Link} from 'react-router-dom'
import useDamImage from '../../../../custom-hooks/use-dam-image'
import {useGetCategoriesData} from '../../../../custom-api/hooks/use-get-categories-data'
import {getGlobalLink, safeBuildUrl} from '../../../../utils/contentstack-utils'
import {remCalc} from '../../../../theme/_utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import SanitizedHtml from '../../../html-sanitizer'
import {useProducts} from '@salesforce/commerce-sdk-react'

export const SortingTile = ({parentKey, styles, tile, variant}) => {
    const damImage = useDamImage(tile?.image)
    const categoriesData = useGetCategoriesData()
    const {data: productsData} = useProducts({
        parameters: {
            ids:
                tile?.button?.productsfcc?.data?.length > 0
                    ? tile?.button.productsfcc.data[0].id
                    : '',
            allImages: false,
        },
    })
    const {locale} = useMultiSite()
    const {url, label, isAbsolute} = getGlobalLink({
        link: tile?.button,
        categoriesData,
        productsData,
        locale: locale?.alias || locale?.id?.toLowerCase(),
    })
    const {buildUrl} = useMultiSite()
    const buttonProps = isAbsolute
        ? {
              as: 'a',
              href: url,
              target: '_blank',
              rel: 'noopener noreferrer',
          }
        : {
              as: Link,
              to: safeBuildUrl(url, buildUrl),
          }

    const spacing_24 = remCalc(24)

    return (
        <Flex data-testid="sorting-tile-testid" {...styles.tile} className={'sorting-tile'}>
            <Box {...styles.tile?.imagecontainer} className={'sorting-resizable'}>
                {(damImage.url || tile.image?.alt) && (
                    <AspectRatio ratio={damImage.ratio} {...styles.tile?.image}>
                        <Img src={damImage.url} alt={tile.image?.alt || ''} loading="lazy" />
                    </AspectRatio>
                )}
            </Box>
            <Box
                {...styles.tile?.title}
                color={variant === 'black' ? 'text.inverse' : 'text.default'}
                className={'sorting-resizable'}
            >
                {tile?.title && <Text>{tile?.title}</Text>}
            </Box>
            <SanitizedHtml
                as={Box}
                html={tile?.body_copy || ''}
                color={variant === 'black' ? 'text.inverse' : 'text.default'}
                {...styles.tile?.bodycopy}
                className={'sorting-resizable'}
            />
            {tile.features?.length > 0 && (
                <Box color={variant === 'black' ? 'text.inverse' : 'text.default'}>
                    <List {...styles.tile?.features}>
                        {tile?.features
                            .filter((feature) => !!feature?.feature?.trim())
                            .map((feature, fIndex) => (
                                <ListItem
                                    key={`${parentKey}-feature-${fIndex}`}
                                    borderTop={fIndex === 0 ? 'none' : '1px solid #efefef'}
                                    {...styles.tile?.feature}
                                    _first={{pt: spacing_24}}
                                    _last={{pb: spacing_24}}
                                    className={'sorting-resizable'}
                                >
                                    {feature?.feature}
                                </ListItem>
                            ))}
                    </List>
                </Box>
            )}
            {label && url && (
                <Button
                    {...buttonProps}
                    {...styles.tile?.cta}
                    variant={variant === 'black' ? 'inverse-primary' : 'primary'}
                >
                    {label}
                </Button>
            )}
        </Flex>
    )
}

SortingTile.propTypes = {
    parentKey: PropTypes.string,
    styles: PropTypes.shape(),
    tile: PropTypes.shape({
        title: PropTypes.string,
        body_copy: PropTypes.string,
        image: PropTypes.shape(),
        features: PropTypes.arrayOf(PropTypes.shape({feature: PropTypes.string})),
        button: PropTypes.shape(),
    }),
    variant: PropTypes.string,
}
